<script setup>
import { getCurrentInstance, computed, ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { store as storeConfig } from "~app_root/public/tv/app.config";
import { Link } from '@inertiajs/vue3'
import { scrollTop } from "@/utility";
import { useCartItem } from "@/Composables/useCartItem";

const appInstance = getCurrentInstance();
const emitter = appInstance.appContext.config.globalProperties.emitter;

const props = defineProps({
    promotions: {
        type: Array,
        required: true,
        default: () => []
    }
});

const currentIndex = ref(0);
const touchStartX = ref(0);
const touchEndX = ref(0);
const isTransitioning = ref(false);
const direction = ref('next');
let autoplayInterval;
const sliderHeight = ref('auto');

const getPromotionStyles = (promotion) => {
    return {
        backgroundColor: promotion.background_color || '#f8f9fa',
        color: promotion.text_color || '#333',
        borderLeft: `4px solid ${promotion.background_color || '#007bff'}`
    };
};

const nextSlide = () => {
    if (isTransitioning.value || props.promotions.length <= 1) return;
    direction.value = 'next';
    isTransitioning.value = true;
    currentIndex.value = (currentIndex.value + 1) % props.promotions.length;
    setTimeout(() => {
        isTransitioning.value = false;
        updateSliderHeight();
    }, 500);
};

const prevSlide = () => {
    if (isTransitioning.value || props.promotions.length <= 1) return;
    direction.value = 'prev';
    isTransitioning.value = true;
    currentIndex.value = currentIndex.value === 0 ? props.promotions.length - 1 : currentIndex.value - 1;
    setTimeout(() => {
        isTransitioning.value = false;
        updateSliderHeight();
    }, 500);
};

const goToSlide = (index) => {
    if (isTransitioning.value || currentIndex.value === index) return;
    direction.value = index > currentIndex.value ? 'next' : 'prev';
    isTransitioning.value = true;
    currentIndex.value = index;
    setTimeout(() => {
        isTransitioning.value = false;
        updateSliderHeight();
    }, 500);
};

const handleTouchStart = (event) => {
    touchStartX.value = event.touches[0].clientX;
};

const handleTouchMove = (event) => {
    touchEndX.value = event.touches[0].clientX;
};

const handleTouchEnd = () => {
    const swipeThreshold = 50;
    const diff = touchEndX.value - touchStartX.value;

    if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
            direction.value = 'prev';
            prevSlide();
        } else {
            direction.value = 'next';
            nextSlide();
        }
    }
};

const startAutoplay = () => {
    if (props.promotions.length <= 1) return;
    autoplayInterval = setInterval(() => {
        nextSlide();
    }, 4000);
};

const stopAutoplay = () => {
    if (autoplayInterval) {
        clearInterval(autoplayInterval);
    }
};

const updateSliderHeight = () => {
    nextTick(() => {
        const promotionElements = document.querySelectorAll('.product-integrated-promotion');
        let maxHeight = 0;
        promotionElements.forEach(el => {
            const height = el.offsetHeight;
            if (height > maxHeight) {
                maxHeight = height;
            }
        });
        sliderHeight.value = maxHeight + 'px';
    });
};

onMounted(() => {
    updateSliderHeight();
    window.addEventListener('resize', updateSliderHeight);
});

onBeforeUnmount(() => {
    stopAutoplay();
    window.removeEventListener('resize', updateSliderHeight);
});
</script>

<template>
    <div v-if="promotions.length" class="product-integrated-promotions">
        <div class="slider-container" @touchstart="handleTouchStart" @touchmove="handleTouchMove"
            @touchend="handleTouchEnd" @mouseenter="stopAutoplay" @mouseleave="startAutoplay">

            <div class="slider-wrapper" :style="{ height: sliderHeight }">
                <transition-group :name="direction === 'next' ? 'slide-next' : 'slide-prev'" tag="div"
                    class="slides-container" @before-enter="updateSliderHeight" @before-leave="updateSliderHeight">
                    <div v-for="(promotion, index) in promotions" :key="index" v-show="currentIndex === index"
                        class="product-integrated-promotion" :style="getPromotionStyles(promotion)">
                        <div class="promotion-content">
                            <h3 class="promotion-title">{{ promotion.title }}</h3>
                            <div class="promotion-text" v-if="promotion.content" v-html="promotion.content"></div>
                        </div>
                    </div>
                </transition-group>
            </div>

            <div v-if="promotions.length > 1" class="slider-pagination">
                <button v-for="(_, index) in promotions" :key="index" class="pagination-dot"
                    :class="{ active: currentIndex === index }" @click="goToSlide(index)">
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "PIP"
};
</script>